import React, { 
    useState, 
    useEffect, 
    useRef, 
    forwardRef, 
    useImperativeHandle 
} from "react";
import { useForm, Controller } from "react-hook-form";
import { AiOutlineUpload } from "react-icons/ai";
import { FiTrash } from "react-icons/fi";

import { compressImage } from "../tool";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { warning, error } from "@alert";
import { CheckoutDetailsPerfectScrollbar } from "@components/CheckoutDetailsScrollbar";
import "@components/CheckoutDetailsScrollbar/checkout-details-scrollbar.scss";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EDIT_INFO_PS_CLASS =
    "edit-warp-info-form-scroll checkout-details-ps--main checkout-details-ps--theme-slate-narrow scrollbar-container";

const EditInfo = forwardRef(function EditInfo(
    { setData, getUrlFromS3, form, setForm, isCreateInfoStep, scrollContainerRef, onPsSync },
    ref
) {
    const [inputValue, setInputValue] = useState('');
    const [dragState, setDragState] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [uploading, setUploading] = useState(false);

    const { control, trigger, setError, clearErrors, formState: { errors }, reset, watch } = useForm({
        defaultValues: {
            branch_name: form.branch_name || '',
            phone: form.phone || '',
            branch_address: form.branch_address || '',
            email: form.email || '',
        },
        mode: 'onSubmit',
    });

    const wName = watch('branch_name');
    const wPhone = watch('phone');
    const wAddress = watch('branch_address');
    const wEmail = watch('email');

    const initializedRef = useRef(false);
    const syncReadyRef = useRef(false);

    useEffect(() => {
        if (initializedRef.current) return;
        initializedRef.current = true;
        reset({
            branch_name: form.branch_name || '',
            phone: form.phone || '',
            branch_address: form.branch_address || '',
            email: form.email || '',
        });
        // 等 reset 後再允許 watch → form 同步，避免初次空值覆寫造成假 dirty
        syncReadyRef.current = true;
    }, [form.branch_name, form.phone, form.branch_address, form.email, reset]);

    useEffect(() => {
        if (!syncReadyRef.current) return;
        setForm((f) => {
            if (
                (f.branch_name || '') === (wName || '') &&
                (f.phone || '') === (wPhone || '') &&
                (f.branch_address || '') === (wAddress || '') &&
                (f.email || '') === (wEmail || '')
            ) {
                return f;
            }
            return {
                ...f,
                branch_name: wName || '',
                phone: wPhone || '',
                branch_address: wAddress || '',
                email: wEmail || '',
            };
        });
        setData((d) => ({
            ...d,
            branch_name: wName || '',
            phone: wPhone || '',
            branch_address: wAddress || '',
            email: wEmail || '',
        }));
    }, [wName, wPhone, wAddress, wEmail, setForm, setData]);

    useImperativeHandle(ref, () => ({
        validateCreateInfo: async () => {
            if (!form.branch_logo_url) {
                setError('branch_logo_url', { type: 'manual', message: '請上傳分店封面' });
            } else {
                clearErrors('branch_logo_url');
            }
            const fieldsOk = await trigger(['branch_name', 'phone', 'branch_address', 'email']);
            return fieldsOk && !!form.branch_logo_url;
        },
        validateEditInfo: async () => {
            if (!form.branch_logo_url) {
                setError('branch_logo_url', { type: 'manual', message: '請上傳分店封面' });
            } else {
                clearErrors('branch_logo_url');
            }
            const fieldsOk = await trigger(['branch_name', 'phone', 'branch_address', 'email']);
            return fieldsOk && !!form.branch_logo_url;
        },
    }), [form.branch_logo_url, trigger, setError, clearErrors]);

    useEffect(() => {
        if (form.branch_logo_url) clearErrors('branch_logo_url');
    }, [form.branch_logo_url, clearErrors]);

    useEffect(() => {
        setImageError(false);
    }, [form.branch_logo_url]);

    const addTag = (tag) => {
        if (form.tagNames.length < 5) {
            setForm((data) => ({
                ...data,
                "tagNames": [...data.tagNames, tag]
            }));
            setData((e) => ({
                ...e,
                "tagNames": [...form.tagNames, tag]
            }));
            setInputValue('');
        }
    };

    const removeTag = (index) => {
        setForm((data) => ({
            ...data,
            "tagNames": data.tagNames.filter((_, i) => i !== index)
        }));
        setData((e) => ({
            ...e,
            "tagNames": form.tagNames.filter((_, i) => i !== index)
        }));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        } else if (e.key === 'Backspace' && inputValue === '' && form.tagNames.length > 0) {
            removeTag(form.tagNames.length - 1);
        }
    };
    const handleBlur = () => {
        if (inputValue.trim() !== '') {
            addTag(inputValue.trim());
            setInputValue('');
        }
    };

    const maxCharacters = 300;

    const handleImageChange = async (event) => {
        const maxFileSize = 2.5 * 1024 * 1024;
        const isEvent = event && event.target && event.target.files;
        const file = isEvent ? event.target.files[0] : event;
        if (isEvent) event.target.value = '';

        if (file) {
            const validTypes = ['image/jpg', 'image/jpeg', 'image/png'];
            if (!validTypes.includes(file.type)) {
                warning(1, "檔案格式錯誤", "僅限 JPG, JPEG, PNG 格式", "我了解了");
                return;
            }

            if (file.size < maxFileSize) {
                try {
                    setImageError(false);
                    setUploading(true);
                    const reSizeImage = await compressImage(file, 1000, 1);
                    try {
                        await getUrlFromS3(reSizeImage);
                        setUploading(false);
                    } catch (uploadErr) {
                        setUploading(false);
                        error(1, "上傳失敗", `${uploadErr.message}`, "確認");
                    }
                } catch (err) {
                    setUploading(false);
                    error(1, `圖片處理失敗, ${err.message}`, "請稍後重試", "確認");
                }
            } else {
                warning(1, "檔案壓縮後仍超過 2.5 MB", "請壓縮檔案後再上傳！", "我了解了");
            }
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragState(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragState(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragState(false);

        const nativeEvent = e.nativeEvent || e;
        const dataTransfer = nativeEvent.dataTransfer;

        if (!dataTransfer) {
            return;
        }

        const files = dataTransfer.files;
        if (files && files.length > 0) {
            await handleImageChange(files[0]);
        }
    };

    const add = (e, v) => {
        switch (v) {
            case 'name':
                setForm((data) => ({
                    ...data,
                    "branch_name": e.target.value
                }));
                setData((i) => ({
                    ...i,
                    "branch_name": e.target.value
                }))
                break;
            case 'phone':
                setForm((data) => ({
                    ...data,
                    "phone": e.target.value
                }));
                setData((i) => ({
                    ...i,
                    "phone": e.target.value
                }))
                break;
            case 'address':
                setForm((data) => ({
                    ...data,
                    "branch_address": e.target.value
                }));
                setData((i) => ({
                    ...i,
                    "branch_address": e.target.value
                }))
                break;
            case 'email':
                setForm((data) => ({
                    ...data,
                    "email": e.target.value
                }))
                setData((i) => ({
                    ...i,
                    "email": e.target.value
                }))
                break;
            case 'text':
                {
                    const textValue = e.target.value;
                    const trimmedValue = textValue.length > maxCharacters
                        ? textValue.substring(0, maxCharacters)
                        : textValue;

                    setForm((data) => ({
                        ...data,
                        "description": trimmedValue
                    }))
                    setData((i) => ({
                        ...i,
                        "description": trimmedValue
                    }))
                }
                break;
            case 'tag':
                {
                    const value = e.target.value;
                    if (value.endsWith(' ')) {
                        const newTag = value.trim();
                        if (newTag) {
                            addTag(newTag);
                        }
                        setInputValue('');
                    } else {
                        setInputValue(value);
                    }
                }
                break;
            default:
                break;
        }
    }

    const coverBlock = (
        <>
            <input
                id="branch-logo-input"
                type="file"
                accept="image/jpg,image/jpeg,image/png"
                onChange={handleImageChange}
                style={{ display: 'none' }}
            />
            <label type='upload'><span>店家封面 <span style={{ color: '#e75264' }}>*</span></span>
                {!form.branch_logo_url && !uploading &&
                    <div
                        className={dragState ? 'drag-over' : ''}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => document.getElementById('branch-logo-input').click()}
                        style={errors.branch_logo_url ? { border: '2px solid #e75264' } : {}}
                    >
                        <AiOutlineUpload size={28} />
                        <p>上傳照片</p>
                        <p>僅限 JPG, JPEG, PNG 格式</p>
                        <p style={{ marginTop: '8px', fontSize: '12px', opacity: 0.7 }}>或拖放圖片至此處</p>
                    </div>
                }
                {uploading && !form.branch_logo_url &&
                    <div>
                        <div className="upload-spinner" />
                        <p style={{ marginTop: 12, color: '#888' }}>上傳中...</p>
                    </div>
                }
            </label>
            {errors.branch_logo_url && (
                <span className="d-block" style={{ marginTop: 8, fontSize: 12, color: '#e75264' }}>{errors.branch_logo_url.message}</span>
            )}
        </>
    );

    const previewBlock = form.branch_logo_url && (
        <div
            className={`image-hover-container ${dragState ? 'drag-over' : ''}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <img
                src={form.branch_logo_url}
                alt="圖片載入失敗"
                className="preview-image"
                onError={() => setImageError(true)}
                style={{ display: imageError ? 'none' : 'block' }}
            />
            {imageError && (
                <div className="preview-image image-error-placeholder">
                    <span>圖片載入失敗</span>
                </div>
            )}
            <div className="image-overlay">
                <input
                    id="branch-logo-replace-input"
                    type="file"
                    accept="image/jpg,image/jpeg,image/png"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                />
                <button
                    type="button"
                    className="overlay-button replace-button"
                    onClick={() => document.getElementById('branch-logo-replace-input').click()}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M17 8L12 3L7 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 3V15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>取代</span>
                </button>
                <button
                    type="button"
                    className="overlay-button delete-button"
                    onClick={() => {
                        setForm((data) => ({
                            ...data,
                            "branch_logo_url": null
                        }));
                        setData((i) => ({
                            ...i,
                            "branch_logo_url": null
                        }))
                    }}
                >
                    <FiTrash size={24} />
                </button>
            </div>
        </div>
    );

    return (
        <div className="edit-warp-content-info">
            <div className="edit-warp-info-form-shell">
                <CheckoutDetailsPerfectScrollbar
                    className={EDIT_INFO_PS_CLASS}
                    containerRef={(el) => {
                        if (scrollContainerRef) {
                            scrollContainerRef.current = el;
                        }
                    }}
                    minOverflowYPx={0}
                    onSync={(ps) => onPsSync?.(ps)}
                >
                    <form onSubmit={(e) => e.preventDefault()}>
                {coverBlock}
                {previewBlock}

                <label>
                    <span>分店名稱 <span style={{ color: '#e75264' }}>*</span></span>
                    <Controller
                        name="branch_name"
                        control={control}
                        rules={{ required: '請填寫分店名稱' }}
                        render={({ field }) => (
                            <input
                                type="text"
                                {...field}
                                placeholder={`${process.env.REACT_APP_MODAL === 'eternals' ? '恆美學' : '黑曜雲'}診所-分店名稱`}
                                style={errors.branch_name ? { borderColor: '#e75264' } : {}}
                            />
                        )}
                    />
                    {errors.branch_name && (
                        <span className="d-block" style={{ marginTop: 4, fontSize: 12, color: '#e75264' }}>{errors.branch_name.message}</span>
                    )}
                </label>

                <label>
                    <span>分店聯絡電話 <span style={{ color: '#e75264' }}>*</span></span>
                    <Controller
                        name="phone"
                        control={control}
                        rules={{ required: '請填寫分店聯絡電話' }}
                        render={({ field }) => (
                            <input
                                type="tel"
                                {...field}
                                placeholder="(02)12-345678"
                                style={errors.phone ? { borderColor: '#e75264' } : {}}
                            />
                        )}
                    />
                    {errors.phone && (
                        <span className="d-block" style={{ marginTop: 4, fontSize: 12, color: '#e75264' }}>{errors.phone.message}</span>
                    )}
                </label>

                <label>
                    <span>分店地址 <span style={{ color: '#e75264' }}>*</span></span>
                    <Controller
                        name="branch_address"
                        control={control}
                        rules={{ required: '請填寫分店地址' }}
                        render={({ field }) => (
                            <input
                                type="text"
                                {...field}
                                placeholder="臺北市 松山區 "
                                style={errors.branch_address ? { borderColor: '#e75264' } : {}}
                            />
                        )}
                    />
                    {errors.branch_address && (
                        <span className="d-block" style={{ marginTop: 4, fontSize: 12, color: '#e75264' }}>{errors.branch_address.message}</span>
                    )}
                </label>

                <label>
                    <span>分店 Email <span style={{ color: '#e75264' }}>*</span></span>
                    <Controller
                        name="email"
                        control={control}
                        rules={{
                            required: '請填寫分店 Email',
                            validate: (v) => EMAIL_RE.test((v || '').trim()) || '請填寫有效的 Email 格式',
                        }}
                        render={({ field }) => (
                            <input
                                type="email"
                                {...field}
                                placeholder="example@gmail.com"
                                style={errors.email ? { borderColor: '#e75264' } : {}}
                            />
                        )}
                    />
                    {errors.email && (
                        <span className="d-block" style={{ marginTop: 4, fontSize: 12, color: '#e75264' }}>{errors.email.message}</span>
                    )}
                </label>

                <label>
                    <div>
                        <p>店家簡介</p>
                        <span>
                            <span style={{ color: form.description && form.description.length >= maxCharacters ? '#e75264' : 'inherit' }}>
                                {form.description ? form.description.length : 0}
                            </span>
                            {' / '}
                            {maxCharacters}
                        </span>
                    </div>
                    <textarea
                        name="description"
                        value={form.description}
                        onChange={(e) => add(e, 'text')}
                        onPaste={(e) => {
                            e.preventDefault();
                            const paste = (e.clipboardData || window.clipboardData).getData('text');
                            const textarea = e.target;
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const currentText = form.description || '';
                            const beforeText = currentText.substring(0, start);
                            const afterText = currentText.substring(end);
                            const newText = beforeText + paste + afterText;
                            const trimmedText = newText.length > maxCharacters
                                ? newText.substring(0, maxCharacters)
                                : newText;

                            setForm((data) => ({
                                ...data,
                                "description": trimmedText
                            }))
                            setData((i) => ({
                                ...i,
                                "description": trimmedText
                            }))

                            setTimeout(() => {
                                const newStart = Math.min(start + paste.length, trimmedText.length);
                                textarea.setSelectionRange(newStart, newStart);
                            }, 0);
                        }}
                        maxLength={maxCharacters}
                    ></textarea>
                </label>

                <label>
                    分店標籤
                    <ul className="tags">
                        {form.tagNames.map((item, index) => (
                            <li key={index}>
                                #{item}
                                <AiOutlineCloseCircle size={14} onClick={() => removeTag(index)} />
                            </li>
                        ))}
                        <input
                            type="tag"
                            name="tag"
                            value={inputValue}
                            onChange={(e) => add(e, 'tag')}
                            onKeyDown={handleKeyDown}
                            onBlur={handleBlur}
                            placeholder={form.tagNames?.length < 1 ? '請填寫標籤名稱，最多填寫5個' : ''}
                        />
                    </ul>
                </label>
                    </form>
                </CheckoutDetailsPerfectScrollbar>
            </div>
        </div>
    );
});

export default EditInfo;
